import { useState } from "react";
import { useQueryClient } from "react-query";
import { CircularProgress } from "@mui/material";
import { useFormContext } from "react-hook-form";

import PrimaryButton from "../../../Components/Buttons/primary-button";
import CloseButton from "../../../Components/Buttons/close-button";
import DatePickerField from "../../../Components/Form Fields/date-picker-field";
import SSNField from "../../../Components/Form Fields/ssn-field";

import renderOptedIn from "./opted-in";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useDeals, useUserAccount } from "../../../CustomHooks/Queries";
import { useOptInCreditReporting } from "../../../CustomHooks/Mutations";

import {
  dispatchAPIErrorToast,
  formatDate,
  getPublicIPAddress,
  replaceNonNumericCharacters,
} from "../../../Utils/helpers";
import { APIQueryKeys, modalVariants } from "../../../Utils/constants";
import { validation } from "../../../Utils/validations";

export default function renderCreditBuilderOptIn(modalContext, props) {
  const { updateModalComponent, setModalVariant, setModalTitle, setModalClassName, setModalAriaLabel } = modalContext;

  updateModalComponent(() => <CreditBuilderOptIn {...props} />);
  setModalVariant(modalVariants.flexDialog);
  setModalTitle("");
  setModalAriaLabel?.("Credit Builder enrollment");
  setModalClassName("credit-builder-enrollment");
}

function BuildCreditIntro({ onContinue, onClose }) {
  return (
    <div className="cb-modal-inner">
      <CloseButton onClick={onClose} className="cb-close-btn" />

      <div className="cb-icon-circle">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path
            d="M16 4L19.5 11H26L20.5 15.5L22.5 23L16 18.5L9.5 23L11.5 15.5L6 11H12.5L16 4Z"
            stroke="#54FF99"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div style={{ height: 24 }} />

      <h2 className="cb-title">Build Credit With Your Rent</h2>

      <div style={{ height: 8 }} />

      <p className="cb-subtitle">
        Report your on-time rent payments to all three credit bureaus and build credit automatically.
      </p>

      <div style={{ height: 24 }} />

      <div className="cb-divider" />

      <div style={{ height: 24 }} />

      <div className="cb-bullets">
        <div className="cb-bullet">
          <div className="cb-dot" />
          <span>Reported monthly to Experian, Equifax & TransUnion</span>
        </div>
        <div className="cb-bullet">
          <div className="cb-dot" />
          <span>No credit check to enroll</span>
        </div>
        <div className="cb-bullet">
          <div className="cb-dot" />
          <span>Cancel anytime in settings</span>
        </div>
      </div>

      <div className="cb-action-bar">
        <PrimaryButton onClick={onContinue}>Continue</PrimaryButton>
      </div>
    </div>
  );
}

function ActivateStep({ onBack, onOptIn, onClose }) {
  const modalContext = useModalContext();

  const { watch, register, handleSubmit, formState, getFieldState, setValue } = useFormContext();

  const queryClient = useQueryClient();

  const { data: deals } = useDeals();
  const { data: userAccountData } = useUserAccount();

  const { isLoading: isCreditReportingOptingIn, mutate: OptInCreditReporting } = useOptInCreditReporting({
    onSuccess: () => {
      onOptIn?.();
      queryClient.refetchQueries(APIQueryKeys.creditReporting);
      renderOptedIn(modalContext);
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "creditBuilderOptIn" }),
  });

  const nameOfTheUser = `${userAccountData?.data?.data?.user?.firstName} ${userAccountData?.data?.data?.user?.lastName}`;

  const dob = watch("dob");
  const ssn = watch("ssn");
  const name = watch("name");
  const consent = watch("consent") ?? false;

  const birthDate = deals?.data?.data?.currentDeal?.birthdate;
  const ssn_token = deals?.data?.data?.currentDeal?.ssn_token__c;
  const executedDate = formatDate(new Date().toISOString(), {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const nameProps = register("name", {
    required: true,
    validate: (val) =>
      val?.trim() === nameOfTheUser?.trim() || "Please make sure the signature matches the name below.",
  });

  const { error: nameFieldError } = getFieldState(nameProps.name, formState);

  const handleActivate = async () => {
    OptInCreditReporting({
      ...(!!dob ? { dob } : {}),
      ...(!!ssn ? { ssn: replaceNonNumericCharacters(ssn) } : {}),
      user_ip: await getPublicIPAddress(),
    });
  };

  const disableActivate =
    isCreditReportingOptingIn || (!dob && !birthDate) || (!ssn && !ssn_token) || !name || !consent;

  return (
    <form className="cb-modal-inner cb-modal-inner--confirm" onSubmit={handleSubmit(handleActivate)} autoComplete="off">
      <CloseButton onClick={onClose} className="cb-close-btn" />

      <div className="cb-enrollment-header">
        <h2 className="cb-title cb-title--left">Confirm Your Credit Builder Enrollment</h2>
        <p className="cb-subtitle cb-subtitle--left">
        Please review and agree to the declaration below. <br/>
        Note: When Stake pulls your credit score data it will not impact your score.
        </p>
      </div>

      <div className="cb-declaration-box">
        <p className="cb-declaration-paragraph">
        I consent to having Stake Network, Inc., on behalf of my rental community, report my on-time rent payments to major credit bureaus.
        </p>
        <p className="cb-declaration-paragraph">
        I understand that this program is optional and I am able to opt-out at any time, subject to a 6-month waiting period to re-enroll. I also understand that a late or missed rent payment, after enrollment, will automatically opt me out of this service, with a 6-month waiting period to re-enroll.
        </p>
        <p className="cb-declaration-paragraph">
        I authorize Stake Network, Inc. to pull my credit score as part of my Credit Builder enrollment. I understand that Stake may use this score to track and measure the effective of the Credit Builder program and to report to it's partners the impact of Credit Builder on improve credit scores. I understand that the credit score pulls may occur more than once and on a regular basis while I am participating in the Credit Builder Program.
        </p>
        <p className="cb-declaration-meta">Executed {executedDate}</p>
        <p className="cb-declaration-meta">By: {nameOfTheUser}</p>
      </div>

      <div className="cb-signature-section">
        <p className="cb-section-label">
          Enter your full name to agree to the above declaration and confirm your enrollment.
        </p>
        <input
          className="cb-signature-field cb-signature-field--line"
          placeholder="Sign here"
          {...nameProps}
          autoComplete="off"
        />
        {nameFieldError?.message && <p className="cb-error">{nameFieldError.message}</p>}
        <p className="cb-sign-hint">Sign as &ldquo;{nameOfTheUser}&rdquo;</p>
      </div>

      {!birthDate && (
        <div className="cb-extra-field">
          <p className="cb-section-label">Date of birth</p>
          <DatePickerField
            name="dob"
            format="MM/DD/YYYY"
            textFieldProps={{ placeholder: "MM/DD/YYYY", fullWidth: true }}
            datePickerProps={{ disableFuture: true }}
            validations={{
              isAValidDate: validation.date.isAValidDate,
              isAbove18YearsOfAge: validation.date.isAbove18YearsOfAge,
            }}
            required="Date of Birth is required"
          />
        </div>
      )}

      {!ssn_token && (
        <div className="cb-extra-field">
          <p className="cb-section-label">Social Security Number</p>
          <SSNField label="" placeholder="XXX-XX-XXXX" required="Social Security Number is required" />
        </div>
      )}

      <div className="cb-consent-row">
        <button
          type="button"
          className={`cb-checkbox${consent ? " checked" : ""}`}
          onClick={() => setValue("consent", !consent)}
          aria-pressed={consent}
          aria-label="Agree to Stake End User Terms and Privacy Policy"
        >
          <svg viewBox="0 0 12 12" fill="none">
            <path d="M2 6L5 9L10 3" stroke="#0F1219" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <p className="cb-consent-text">
          By checking this box, I attest to have read and agree to Stake&apos;s{" "}
          <a
            href="https://www.stakerent.com/legal/end-user-terms"
            target="_blank"
            rel="noreferrer"
            className="cb-terms-link"
          >
            End User Terms
          </a>{" "}
          and{" "}
          <a
            href="https://www.stakerent.com/legal/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="cb-terms-link"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>

      <div className="cb-action-bar cb-action-bar--split">
        <button type="button" className="cb-secondary-btn" onClick={onBack}>
          Back
        </button>
        <PrimaryButton disabled={disableActivate} type="submit" className="cb-confirm-btn">
          Confirm {isCreditReportingOptingIn && <CircularProgress />}
        </PrimaryButton>
      </div>
    </form>
  );
}

const CreditBuilderOptIn = ({ onOptIn }) => {
  const [step, setStep] = useState(1);
  const { onModalClose } = useModalContext();

  if (step === 1) {
    return <BuildCreditIntro onContinue={() => setStep(2)} onClose={onModalClose} />;
  }

  return <ActivateStep onBack={() => setStep(1)} onOptIn={onOptIn} onClose={onModalClose} />;
};
